import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import styles from "@/components/quote.module.css";
import { getNewNoteTemplate } from "@/data/new-note-template";
import * as signals from "@/signals";
import {
	closeMenu,
	copyText,
	handleKeyboardClick,
	search,
	showMenu,
} from "@/utils";
import {
	faCopy,
	faHeart,
	faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import Waves from "node-waves";
import { JSX, MouseEvent, useEffect, useState } from "react";

function Quote(): JSX.Element {
	const queryClient = useQueryClient();

	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

	const { data } = useQuery({
		queryFn: api.getHitokoto,
		queryKey: ["quote"],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});

	const handleCloseMenu = (): void => {
		void closeMenu();
	};

	const showQuoteMenu = (event: MouseEvent<HTMLButtonElement>): void => {
		event.stopPropagation();
		if (!data) {
			return;
		}
		if (signals.menu.value) {
			handleCloseMenu();
			return;
		}
		const fullQuote = `${data.hitokoto}—— ${data.from}`;
		showMenu(event, [
			{
				content: {
					icon: faCopy,
					text: t("copy"),
				},
				onClick: (): void => {
					void copyText(fullQuote);
				},
				when: true,
			},
			{
				content: {
					icon: faMagnifyingGlass,
					text: t("search"),
				},
				onClick: (): void => {
					search(fullQuote);
				},
				when: true,
			},
			{
				content: {
					icon: faHeart,
					text: t("addToNotes"),
				},
				onClick: async (): Promise<void> => {
					signals.isLoadingShown.value = true;
					try {
						await new QueryDataManager(queryClient).createNote({
							...getNewNoteTemplate(),
							content: fullQuote,
						});
						signals.greetingText.value =
							t("addedToNotes") + " ￣ω￣=";
					} catch (error) {
						void api.handleApiError(error);
					} finally {
						signals.isLoadingShown.value = false;
					}
				},
				when: true,
			},
		]);
	};

	useEffect(() => {
		Waves.init();
	}, []);

	useEffect(() => {
		if (data) {
			setIsDataLoaded(true);
		}
	}, [data]);

	if (!data || !signals.generalSettings.value.isQuoteEnabled) {
		return <></>;
	}
	return (
		<div
			className={clsx(
				(!signals.isSearchFocused.value || !isDataLoaded) &&
					styles["hide"],
				styles["quote-container"],
				"waves-effect",
				"waves-light",
			)}
			onClick={handleCloseMenu}
			onKeyDown={handleKeyboardClick(handleCloseMenu)}
			role="button"
			tabIndex={0}
		>
			<div
				className={clsx(
					signals.generalSettings.value.isLiteModeEnabled && "lite",
					styles["quote-background"],
				)}
			></div>
			<div className={styles["quote-content"]}>
				<span>「</span>
				{data.hitokoto}
				<span>」</span>
			</div>
			<div className={styles["quote-source"]}>—— {data.from}</div>
			<button
				aria-haspopup="menu"
				className={styles["quote-menu-button"]}
				title={t("options")}
				type="button"
				onClick={showQuoteMenu}
			>
				<i>&#xe66e;</i>
			</button>
		</div>
	);
}

export default Quote;
